import { startOfDay } from "../../../../../_shared/_logic/date-math"
import { parseMinutes, type WorkingHoursByUser } from "../../../../../_shared/_logic/form-state"
import type { CalendarBoardExternalBusyInterval } from "../../../../../api/kehrblatt/schedule-events"

export type CalendarHourBounds = {
  earliestStartMinutes: number
  latestEndMinutes: number
  dayStartHour: number
  dayEndHour: number
}

export const toInputDate = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export const parseInputDate = (value: unknown): Date | null => {
  if (value instanceof Date) return startOfDay(value)

  if (value && typeof value === "object") {
    const detailValue = (value as { detail?: { value?: unknown } })?.detail?.value

    if (detailValue !== undefined) return parseInputDate(detailValue)

    const targetValue = (value as { target?: { value?: unknown } })?.target?.value

    if (targetValue !== undefined) return parseInputDate(targetValue)
  }

  if (typeof value !== "string") return null

  const [yearRaw, monthRaw, dayRaw] = value.split("-")
  const year = Number(yearRaw)
  const month = Number(monthRaw)
  const day = Number(dayRaw)

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null

  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  const parsed = new Date(year, month - 1, day)

  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day)
    return null

  return parsed
}

export const parseDateLike = (value: unknown): Date | null => {
  if (value == null) return null

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null

    return value
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null

    const parsed = new Date(value)

    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  if (typeof value === "string") {
    const parsed = new Date(value)

    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}

const clampValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export const resolveCalendarHourBounds = ({
  targetEmployeeIds,
  workingHoursByUser,
  filteredEvents,
  externalBusyIntervals = [],
  fallbackDayStartHour,
  fallbackDayEndHour,
}: {
  targetEmployeeIds: string[]
  workingHoursByUser: WorkingHoursByUser
  filteredEvents: ScheduleEvent[]
  externalBusyIntervals?: CalendarBoardExternalBusyInterval[]
  fallbackDayStartHour: number
  fallbackDayEndHour: number
}): CalendarHourBounds => {
  const fallbackStartMinutes = fallbackDayStartHour * 60
  const fallbackEndMinutes = fallbackDayEndHour * 60

  let workingStartMinutes: number | null = null
  let workingEndMinutes: number | null = null

  for (const employeeId of targetEmployeeIds) {
    const schedule = workingHoursByUser?.[employeeId] ?? []
    for (const day of schedule) {
      if (!day?.active) continue

      const start = parseMinutes(day.start_time)
      const end = parseMinutes(day.end_time)

      if (start === null || start === undefined || end === null || end === undefined) continue

      if (end <= start) continue

      workingStartMinutes =
        workingStartMinutes === null ? start : Math.min(workingStartMinutes, start)
      workingEndMinutes = workingEndMinutes === null ? end : Math.max(workingEndMinutes, end)
    }
  }

  const baseStart = workingStartMinutes ?? fallbackStartMinutes
  const baseEnd = workingEndMinutes ?? fallbackEndMinutes

  let effectiveStart = baseStart
  let effectiveEnd = baseEnd

  for (const event of filteredEvents ?? []) {
    if ((event as { all_day?: boolean })?.all_day) continue

    const isSoft = (event as { flex_type?: ScheduleEventFlexType })?.flex_type === "SOFT"
    const startValue =
      isSoft && event.timeframe_start_at ? event.timeframe_start_at : event.start_at
    const endValue = isSoft && event.timeframe_end_at ? event.timeframe_end_at : event.end_at
    const start = new Date(startValue)
    const end = new Date(endValue)

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) continue

    const startKey = toInputDate(startOfDay(start))
    const endKey = toInputDate(startOfDay(end))

    if (startKey !== endKey) continue

    const startMinutes = start.getHours() * 60 + start.getMinutes()
    const endMinutes = end.getHours() * 60 + end.getMinutes()

    if (endMinutes <= startMinutes) continue

    effectiveStart = Math.min(effectiveStart, startMinutes)
    effectiveEnd = Math.max(effectiveEnd, endMinutes)
  }

  for (const interval of externalBusyIntervals) {
    if (interval.all_day) continue

    const start = new Date(interval.start_at)
    const end = new Date(interval.end_at)

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) continue

    if (toInputDate(startOfDay(start)) !== toInputDate(startOfDay(end))) continue

    const startMinutes = start.getHours() * 60 + start.getMinutes()
    const endMinutes = end.getHours() * 60 + end.getMinutes()

    if (endMinutes <= startMinutes) continue

    effectiveStart = Math.min(effectiveStart, startMinutes)
    effectiveEnd = Math.max(effectiveEnd, endMinutes)
  }

  let startHour = clampValue(Math.floor(effectiveStart / 60), 0, 23)
  let endHour = clampValue(Math.ceil(effectiveEnd / 60), 0, 23)

  if (endHour <= startHour) {
    startHour = clampValue(startHour, 0, 22)
    endHour = startHour + 1
  }

  return {
    earliestStartMinutes: effectiveStart,
    latestEndMinutes: effectiveEnd,
    dayStartHour: startHour,
    dayEndHour: endHour,
  }
}
